// src/parsers/gerberDetection.ts
// Comprehensive Gerber file detection and layer classification

import type { FileRole } from '../types';

// ============================================================================
// 1. GERBER FILE EXTENSION WHITELIST
// ============================================================================

/**
 * Comprehensive list of Gerber-related file extensions
 * Case-insensitive matching
 * 
 * HINTS ONLY - extensions are not authoritative, content sniffing is required
 */
export const GERBER_EXTENSION_WHITELIST = new Set([
  // Classic KiCad/Altium extensions
  '.gtl',  // Top copper
  '.gbl',  // Bottom copper
  '.gts',  // Top soldermask
  '.gbs',  // Bottom soldermask
  '.gto',  // Top silkscreen (legend)
  '.gbo',  // Bottom silkscreen (legend)
  '.gtp',  // Top paste
  '.gbp',  // Bottom paste
  '.gko',  // Keep-out / edge cuts
  '.gml',  // Mill / board outline
  '.gm1',  // Mechanical layer 1 (often outline)
  '.gm2',  // Mechanical layer 2
  '.gm3',  // Mechanical layer 3
  '.gm4',  // Mechanical layer 4
  '.gm5',  // Mechanical layer 5
  '.gm6',  // Mechanical layer 6
  '.gm7',  // Mechanical layer 7
  '.gm8',  // Mechanical layer 8
  '.gm9',  // Mechanical layer 9
  '.gm10', // Mechanical layer 10
  '.gm11', // Mechanical layer 11
  '.gm12', // Mechanical layer 12
  '.gm13', // Mechanical layer 13
  '.gm14', // Mechanical layer 14
  '.gm15', // Mechanical layer 15
  '.gm16', // Mechanical layer 16
  
  // Inner layer variants (4+ layer boards)
  '.g1',   // Inner copper layer 1
  '.g2',   // Inner copper layer 2
  '.g3',   // Inner copper layer 3
  '.g4',   // Inner copper layer 4
  '.g5',   // Inner copper layer 5
  '.g6',   // Inner copper layer 6
  '.g7',   // Inner copper layer 7
  '.g8',   // Inner copper layer 8
  '.gl1',  // Inner layer 1 (alternative)
  '.gl2',  // Inner layer 2 (alternative)
  '.gp1',  // Power plane 1
  '.gp2',  // Power plane 2
  
  // Generic extensions
  '.gbr',  // Generic Gerber
  '.ger',  // Gerber alternative
  '.gerber', // Full word extension
  '.art',  // Allegro/OrCAD artwork
  '.pho',  // Photo plotter (legacy)
  '.rep',  // Report/output file
  
  // Drill file extensions (often grouped with Gerbers)
  '.drl',  // NC drill
  '.txt',  // Drill report (ambiguous)
  '.tap',  // Tap file (drill)
  '. excellon', // Excellon drill
  
  // Additional manufacturer variants
  '.top',  // Top layer (generic)
  '.bot',  // Bottom layer (generic)
  '.smt',  // Solder mask top
  '.smb',  // Solder mask bottom
  '.sst',  // Silk screen top
  '.ssb',  // Silk screen bottom
  '.spt',  // Solder paste top
  '.spb',  // Solder paste bottom
  '.out',  // Outline (generic)
  '.bor',  // Board outline
  '.edge', // Edge cuts
  '.fab',  // Fabrication drawing
  '.assy', // Assembly drawing
  '.dwg',  // Drawing (ambiguous)
  '.cmp',  // Component layer
  '.sol',  // Solder side (old)
  '.plc',  // Placement
  '.pls',  // Placement silk
  '.stc',  // Stencil (top)
  '.sts',  // Stencil (bottom)
  '.drd',  // Drill drawing
  '.nc',   // NC tool path
  '.cnc',  // CNC drill
  '.rou',  // Route file
  '.milling', // Milling
]);

/**
 * Check if filename has a Gerber-like extension
 * Returns true if extension is in whitelist (case-insensitive)
 * 
 * NOTE: This is a HINT only - content sniffing is required for confirmation
 */
export function hasGerberExtension(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  return GERBER_EXTENSION_WHITELIST.has(ext);
}

// ============================================================================
// 2. CONTENT-BASED GERBER DETECTION
// ============================================================================

/**
 * RS-274X Gerber format tokens for content sniffing
 */
const GERBER_TOKENS = {
  // Format specification (required in RS-274X)
  formatSpec: /%FSLAX\d+Y\d+/i,
  
  // Coordinate format
  coordFormat: /%FS(LAX|LAY)\d+/i,
  
  // Units
  unitsInch: /%MOIN/i,
  unitsMetric: /%MOMM/i,
  
  // Aperture definitions
  apertureDef: /%ADD\d+/i,
  apertureMacro: /%AM/i,
  
  // D-code operations
  dCodeOps: /D0[1-3]\*/,
  
  // Interpolation modes
  linear: /G0?1\*/,
  circularCW: /G0?2\*/,
  circularCCW: /G0?3\*/,
  
  // Region mode
  region: /G3[67]/,
  
  // Standard M-codes
  endOfFile: /M0?2\*/,
  
  // File attributes (modern RS-274X)
  fileFunction: /%TF\.FileFunction/i,
  filePolarity: /%TF\.FilePolarity/i,
  
  // Generation software
  generationSoftware: /%TF\.GenerationSoftware/i,
  
  // Comments (G04)
  comment: /G04/i,
};

/**
 * Excellon drill format tokens (for negative confirmation)
 */
const EXCELLON_TOKENS = {
  // Header start
  headerStart: /^M48/i,
  
  // Tool definitions
  toolDef: /^T\d+C[\d.]+/m,
  
  // Drill modes
  drillMode: /^(T\d+|X[\d.]+|Y[\d.]+)/m,
  
  // Header end
  headerEnd: /^M95|^%/m,
  
  // Routing commands
  route: /^R/i,
};

/**
 * Check if file content indicates a Gerber RS-274X file
 * Does NOT rely on filename or folder structure
 * 
 * Returns confidence score 0-1 based on detected tokens
 */
export function isGerberByContent(content: string): number {
  let confidence = 0;
  let checks = 0;
  
  // Must have format specification (strong indicator)
  if (GERBER_TOKENS.formatSpec.test(content)) {
    confidence += 0.4;
  }
  checks++;
  
  // Units declaration
  if (GERBER_TOKENS.unitsInch.test(content) || GERBER_TOKENS.unitsMetric.test(content)) {
    confidence += 0.2;
  }
  checks++;
  
  // Aperture definitions
  if (GERBER_TOKENS.apertureDef.test(content)) {
    confidence += 0.15;
  }
  checks++;
  
  // D-code operations
  if (GERBER_TOKENS.dCodeOps.test(content)) {
    confidence += 0.15;
  }
  checks++;
  
  // File attributes (modern Gerber)
  if (GERBER_TOKENS.fileFunction.test(content)) {
    confidence += 0.1;
  }
  checks++;
  
  // Check for Excellon indicators (negative signal)
  if (EXCELLON_TOKENS.headerStart.test(content)) {
    confidence -= 0.5; // Strong negative
  }
  if (EXCELLON_TOKENS.toolDef.test(content)) {
    confidence -= 0.3;
  }
  
  // Must have some Gerber indicators
  if (checks < 2) {
    confidence *= 0.5;
  }
  
  return Math.max(0, Math.min(1, confidence));
}

/**
 * Distinguish Gerber from Excellon drill files
 * Returns 'gerber' | 'excellon' | 'unknown'
 */
export function classifyFileType(content: string): 'gerber' | 'excellon' | 'unknown' {
  const gerberScore = isGerberByContent(content);
  
  // Check for Excellon indicators
  let excellonScore = 0;
  if (EXCELLON_TOKENS.headerStart.test(content)) excellonScore += 0.4;
  if (EXCELLON_TOKENS.toolDef.test(content)) excellonScore += 0.3;
  if (EXCELLON_TOKENS.headerEnd.test(content)) excellonScore += 0.2;
  
  if (gerberScore > 0.6 && gerberScore > excellonScore) {
    return 'gerber';
  } else if (excellonScore > 0.5) {
    return 'excellon';
  }
  
  return 'unknown';
}

// ============================================================================
// 3. LAYER ROLE CLASSIFICATION
// ============================================================================

/**
 * Modern Gerber file function attributes
 * Format: %TF.FileFunction,<function>,<side>[,<layer>]*%
 * 
 * Examples:
 * - %TF.FileFunction,Copper,L1,Top*%
 * - %TF.FileFunction,Soldermask,Top*%
 * - %TF.FileFunction,Legend,Top*%
 * - %TF.FileFunction,Paste,Top*%
 * - %TF.FileFunction,Profile,NP*%
 */
export interface FileFunctionAttributes {
  function: string;  // Copper, Soldermask, Legend, Paste, Profile, etc.
  side: 'top' | 'bottom' | 'inner' | 'none' | 'np' | null;
  layer: number | null;  // Layer number for copper (L1, L2, etc.)
  polarity: 'positive' | 'negative' | null;
}

/**
 * Extract FileFunction attributes from Gerber content
 * Returns null if not found
 */
export function extractFileFunctionAttributes(content: string): FileFunctionAttributes | null {
  // Match FileFunction attribute
  const match = content.match(/%TF\.FileFunction,([^,\*]+)(?:,([^,\*]+))?(?:,([^,\*]+))?\*%/i);
  
  if (!match) return null;
  
  const fileFunc = match[1]?.trim();
  const sideOrLayer = match[2]?.trim();
  const layerOrPolarity = match[3]?.trim();
  
  if (!fileFunc) return null;
  
  const result: FileFunctionAttributes = {
    function: fileFunc,
    side: null,
    layer: null,
    polarity: null
  };
  
  // Parse side/layer
  if (sideOrLayer) {
    const sideLower = sideOrLayer.toLowerCase();
    
    if (sideLower === 'top' || sideLower === 'l1') {
      result.side = 'top';
    } else if (sideLower === 'bottom' || sideLower === 'bot') {
      result.side = 'bottom';
    } else if (sideLower === 'np' || sideLower === 'none') {
      result.side = 'np';
    } else if (sideLower.startsWith('l')) {
      // Inner layer like L2, L3, etc.
      const layerNum = parseInt(sideLower.slice(1), 10);
      if (!isNaN(layerNum)) {
        result.side = 'inner';
        result.layer = layerNum;
      }
    } else if (sideLower === 'inner') {
      result.side = 'inner';
    }
  }
  
  // Parse layer number if present in third position
  if (layerOrPolarity) {
    if (layerOrPolarity.toLowerCase().startsWith('l')) {
      const layerNum = parseInt(layerOrPolarity.slice(1), 10);
      if (!isNaN(layerNum)) {
        result.layer = layerNum;
      }
    } else if (layerOrPolarity.toLowerCase() === 'positive' || layerOrPolarity.toLowerCase() === 'pos') {
      result.polarity = 'positive';
    } else if (layerOrPolarity.toLowerCase() === 'negative' || layerOrPolarity.toLowerCase() === 'neg') {
      result.polarity = 'negative';
    }
  }
  
  return result;
}

/**
 * Extract role from G04 comments as fallback
 * Common patterns in comments
 */
export function extractRoleFromComments(content: string): { role: string; side: 'top' | 'bottom' | null } | null {
  const lines = content.split('\n');
  
  for (const line of lines) {
    const commentMatch = line.match(/G04\s*(.+)/i);
    if (!commentMatch) continue;
    
    const comment = commentMatch[1].toLowerCase();
    
    // Top side indicators
    if (comment.includes('top') || comment.includes('front')) {
      // Determine role
      if (comment.includes('copper') || comment.includes('layer')) {
        return { role: 'copper', side: 'top' };
      } else if (comment.includes('soldermask') || comment.includes('solder mask') || comment.includes('mask')) {
        return { role: 'soldermask', side: 'top' };
      } else if (comment.includes('silk') || comment.includes('legend') || comment.includes('overlay')) {
        return { role: 'silkscreen', side: 'top' };
      } else if (comment.includes('paste') || comment.includes('solderpaste')) {
        return { role: 'paste', side: 'top' };
      }
    }
    
    // Bottom side indicators
    if (comment.includes('bottom') || comment.includes('back') || comment.includes('bside')) {
      if (comment.includes('copper') || comment.includes('layer')) {
        return { role: 'copper', side: 'bottom' };
      } else if (comment.includes('soldermask') || comment.includes('solder mask') || comment.includes('mask')) {
        return { role: 'soldermask', side: 'bottom' };
      } else if (comment.includes('silk') || comment.includes('legend') || comment.includes('overlay')) {
        return { role: 'silkscreen', side: 'bottom' };
      } else if (comment.includes('paste') || comment.includes('solderpaste')) {
        return { role: 'paste', side: 'bottom' };
      }
    }
    
    // Outline/Profile
    if (comment.includes('outline') || comment.includes('profile') || comment.includes('edge') || comment.includes('board shape')) {
      return { role: 'outline', side: null };
    }
  }
  
  return null;
}

/**
 * Infer role from filename extension (last resort)
 */
export function inferRoleFromFilename(filename: string): { role: string; side: 'top' | 'bottom' | 'inner' | null } | null {
  const ext = filename.slice(filename.lastIndexOf('.')).toLowerCase();
  const base = filename.toLowerCase();
  
  // Top extensions
  if (ext === '.gtl' || ext === '.top' || base.includes('top_copper') || base.includes('front')) {
    return { role: 'copper', side: 'top' };
  }
  if (ext === '.gts' || ext === '.smt' || base.includes('top_mask') || base.includes('tmask')) {
    return { role: 'soldermask', side: 'top' };
  }
  if (ext === '.gto' || ext === '.sst' || base.includes('top_silk') || base.includes('tsilk') || base.includes('legend')) {
    return { role: 'silkscreen', side: 'top' };
  }
  if (ext === '.gtp' || ext === '.spt' || base.includes('top_paste') || base.includes('tpaste')) {
    return { role: 'paste', side: 'top' };
  }
  
  // Bottom extensions
  if (ext === '.gbl' || ext === '.bot' || base.includes('bottom_copper') || base.includes('back')) {
    return { role: 'copper', side: 'bottom' };
  }
  if (ext === '.gbs' || ext === '.smb' || base.includes('bottom_mask') || base.includes('bmask')) {
    return { role: 'soldermask', side: 'bottom' };
  }
  if (ext === '.gbo' || ext === '.ssb' || base.includes('bottom_silk') || base.includes('bsilk')) {
    return { role: 'silkscreen', side: 'bottom' };
  }
  if (ext === '.gbp' || ext === '.spb' || base.includes('bottom_paste') || base.includes('bpaste')) {
    return { role: 'paste', side: 'bottom' };
  }
  
  // Outline/Edge cuts
  if (ext === '.gko' || ext === '.gml' || ext === '.gm1' || ext === '.edge' || ext === '.bor' || base.includes('outline') || base.includes('profile')) {
    return { role: 'outline', side: null };
  }
  
  // Inner layers
  if (ext.match(/^\.g\d+$/) || ext.match(/^\.gl\d+$/)) {
    return { role: 'copper', side: 'inner' };
  }
  
  return null;
}

// ============================================================================
// 4. LAYER STACKING FOR TOP/BOTTOM VIEWS
// ============================================================================

/**
 * Layer stacking order for assembly viewing
 * Layers are rendered from bottom to top (bottom layer first)
 */
export const LAYER_STACK_ORDER = {
  top: [
    'outline',      // Board profile (bottom-most)
    'copper',       // Top copper traces
    'soldermask',   // Top soldermask (covers copper)
    'paste',        // Top solder paste (on top of soldermask)
    'silkscreen',   // Top silkscreen (top-most)
  ],
  bottom: [
    'outline',      // Board profile
    'copper',       // Bottom copper
    'soldermask',   // Bottom soldermask
    'paste',        // Bottom solder paste
    'silkscreen',   // Bottom silkscreen
  ]
} as const;

/**
 * Layer importance for hand assembly
 */
export const LAYER_IMPORTANCE = {
  essential: ['outline', 'copper', 'silkscreen'],
  recommended: ['soldermask'],
  optional: ['paste', 'drills'],
} as const;

/**
 * Gerber candidate before classification
 */
export interface GerberCandidate {
  id: string;
  name: string;
  path: string;
  content: string;
  size: number;
}

/**
 * Confirmed Gerber layer with role and side
 */
export interface ConfirmedGerberLayer {
  id: string;
  name: string;
  path: string;
  role: 'copper' | 'soldermask' | 'silkscreen' | 'paste' | 'outline' | 'drill' | 'other';
  side: 'top' | 'bottom' | 'inner' | 'none';
  layerNumber?: number;  // For multi-layer boards
  polarity: 'positive' | 'negative';
  content: string;
  svgContent: string;  // Parsed SVG
  confidence: number;
  detectionMethod: 'attributes' | 'comments' | 'filename' | 'unknown';
}

/**
 * Layer stack for top or bottom view
 */
export interface LayerStack {
  side: 'top' | 'bottom';
  layers: ConfirmedGerberLayer[];
  width: number;
  height: number;
  outline: Array<{ x: number; y: number }>;
}

/**
 * Complete board view with both sides
 */
export interface BoardLayerStack {
  top: LayerStack;
  bottom: LayerStack;
  drills: Array<{
    x: number;
    y: number;
    diameter: number;
    plated: boolean;
  }>;
}

/**
 * Classify a Gerber layer from file content and metadata
 */
export function classifyGerberLayer(
  candidate: GerberCandidate
): ConfirmedGerberLayer {
  const content = candidate.content;
  
  // Try 1: Modern FileFunction attributes
  const attributes = extractFileFunctionAttributes(content);
  if (attributes) {
    return createConfirmedLayer(candidate, attributes, 'attributes');
  }
  
  // Try 2: G04 comments
  const commentRole = extractRoleFromComments(content);
  if (commentRole) {
    return createConfirmedLayer(candidate, {
      function: commentRole.role,
      side: commentRole.side || 'top',
      layer: null,
      polarity: 'positive'
    }, 'comments');
  }
  
  // Try 3: Filename hints (last resort)
  const filenameRole = inferRoleFromFilename(candidate.name);
  if (filenameRole) {
    return createConfirmedLayer(candidate, {
      function: filenameRole.role,
      side: filenameRole.side || 'top',
      layer: null,
      polarity: 'positive'
    }, 'filename');
  }
  
  // Unknown - default to other
  return {
    id: candidate.id,
    name: candidate.name,
    path: candidate.path,
    role: 'other',
    side: 'none',
    polarity: 'positive',
    content,
    svgContent: '',
    confidence: 0.1,
    detectionMethod: 'unknown'
  };
}

function createConfirmedLayer(
  candidate: GerberCandidate,
  attrs: FileFunctionAttributes,
  method: 'attributes' | 'comments' | 'filename' | 'unknown'
): ConfirmedGerberLayer {
  // Map function to role
  let role: ConfirmedGerberLayer['role'] = 'other';
  
  switch (attrs.function.toLowerCase()) {
    case 'copper':
    case 'signal':
    case 'mixed':
    case 'power':
    case 'ground':
      role = 'copper';
      break;
    case 'soldermask':
    case 'solder_mask':
    case 'mask':
      role = 'soldermask';
      break;
    case 'legend':
    case 'silkscreen':
    case 'overlay':
    case 'silk':
      role = 'silkscreen';
      break;
    case 'paste':
    case 'solderpaste':
      role = 'paste';
      break;
    case 'profile':
    case 'outline':
    case 'edge_cuts':
      role = 'outline';
      break;
    case 'drill':
    case 'drilldrawing':
      role = 'drill';
      break;
  }
  
  // Determine side
  let side: ConfirmedGerberLayer['side'] = 'none';
  if (attrs.side === 'top') side = 'top';
  else if (attrs.side === 'bottom') side = 'bottom';
  else if (attrs.side === 'inner') side = 'inner';
  
  // For copper layers, use layer number to determine if inner
  if (role === 'copper' && attrs.layer && attrs.layer > 1) {
    side = 'inner';
  }
  
  return {
    id: candidate.id,
    name: candidate.name,
    path: candidate.path,
    role,
    side,
    layerNumber: attrs.layer || undefined,
    polarity: attrs.polarity || 'positive',
    content: candidate.content,
    svgContent: '', // To be filled by parser
    confidence: method === 'attributes' ? 0.95 : method === 'comments' ? 0.75 : 0.5,
    detectionMethod: method
  };
}

/**
 * Build layer stack for top or bottom view
 */
export function buildLayerStack(
  layers: ConfirmedGerberLayer[],
  side: 'top' | 'bottom'
): LayerStack {
  // Filter layers for this side
  const sideLayers = layers.filter(l => 
    l.side === side || l.side === 'none'
  );
  
  // Sort by stacking order
  const order = LAYER_STACK_ORDER[side];
  const sortedLayers = sideLayers.sort((a, b) => {
    const aIndex = order.indexOf(a.role as any);
    const bIndex = order.indexOf(b.role as any);
    return aIndex - bIndex;
  });
  
  // Extract board outline from outline layer or calculate bounds
  let outline: Array<{ x: number; y: number }> = [];
  const outlineLayer = sortedLayers.find(l => l.role === 'outline');
  if (outlineLayer) {
    // TODO: Parse outline from Gerber
  }
  
  // Calculate board dimensions
  let width = 100;
  let height = 100;
  
  return {
    side,
    layers: sortedLayers,
    width,
    height,
    outline
  };
}

/**
 * Resolve multiple outline candidates
 * Prefers files with explicit Profile function attribute
 */
export function resolveOutlineLayer(layers: ConfirmedGerberLayer[]): ConfirmedGerberLayer | null {
  // Prefer Profile with FileFunction attributes
  const profileWithAttrs = layers.find(l => 
    l.role === 'outline' && l.detectionMethod === 'attributes'
  );
  if (profileWithAttrs) return profileWithAttrs;
  
  // Prefer .gko or .gm1 extensions
  const standardOutline = layers.find(l => {
    const ext = l.name.toLowerCase();
    return l.role === 'outline' && (ext.endsWith('.gko') || ext.endsWith('.gm1'));
  });
  if (standardOutline) return standardOutline;
  
  // Any outline layer
  return layers.find(l => l.role === 'outline') || null;
}

/**
 * Process a collection of Gerber candidates into a complete board stack
 */
export function processGerberLayers(candidates: GerberCandidate[]): BoardLayerStack {
  // Classify all candidates
  const classified = candidates.map(c => classifyGerberLayer(c));
  
  // Resolve outline if multiple candidates
  const outlineLayer = resolveOutlineLayer(classified);
  
  // Build stacks for both sides
  const topStack = buildLayerStack(classified, 'top');
  const bottomStack = buildLayerStack(classified, 'bottom');
  
  // Extract drill holes (from Excellon or Gerber drill files)
  const drills: Array<{ x: number; y: number; diameter: number; plated: boolean }> = [];
  // TODO: Parse drill files
  
  return {
    top: topStack,
    bottom: bottomStack,
    drills
  };
}
